/**
 * SearchPopup — Command palette / search overlay.
 *
 * Triggered by: clicking header search bar or Ctrl+K / ⌘K.
 * Items sourced from config/navItems.ts — edit that file to sync all search UIs.
 *
 * FIX (React error #130): icons are Hugeicons DATA OBJECTS, not React components.
 * Must use <HugeiconsIcon icon={item.icon} />, never <item.icon />.
 */
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight01Icon as ArrowRight, Search01Icon as Search } from '@hugeicons/core-free-icons';
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { mainNavItems, settingsNavItems, type NavItem } from "@/config/navItems";

export function SearchPopup() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const allItems = useMemo<NavItem[]>(() => [...mainNavItems, ...settingsNavItems], []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); setOpen(o => !o); }
      if (e.key === "Escape" && open) setOpen(false);
    };
    const handleOpen = () => setOpen(true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("OPEN_COMMAND_MENU", handleOpen);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("OPEN_COMMAND_MENU", handleOpen);
    };
  }, [open]);

  useEffect(() => {
    if (open) { setQuery(""); setSelectedIndex(0); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase();
    return allItems.filter(item =>
      item.title.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q)
    );
  }, [query, allItems]);

  const grouped = useMemo(() => {
    const groups: { label: string; items: NavItem[] }[] = [];
    const main     = filtered.filter(i => i.group === 'main');
    const settings = filtered.filter(i => i.group === 'settings');
    if (main.length)     groups.push({ label: 'Navigation', items: main });
    if (settings.length) groups.push({ label: 'Settings',   items: settings });
    return groups;
  }, [filtered]);

  const flatItems = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, flatItems.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const item = flatItems[selectedIndex]; if (item) selectItem(item); }
  }, [flatItems, selectedIndex]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${selectedIndex}"]`)?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  useEffect(() => { setSelectedIndex(0); }, [query]);

  const selectItem = (item: NavItem) => { setOpen(false); navigate(item.url); };

  if (!open) return null;

  let counter = 0;

  return (
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-[560px] mx-4 bg-neutral-900/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/[0.06] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-5 h-14 border-b border-white/[0.06]">
          <HugeiconsIcon icon={Search} className="h-4 w-4 text-muted-foreground/60 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, services, settings..."
            className="flex-1 bg-transparent text-[15px] text-white placeholder:text-muted-foreground/50 outline-none"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="hidden sm:flex items-center h-5 px-1.5 bg-white/[0.06] rounded text-[10px] text-muted-foreground/60 font-mono">ESC</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[50vh] overflow-y-auto py-2">
          {flatItems.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-sm text-muted-foreground/60">No results for "{query}"</p>
            </div>
          ) : grouped.map(group => (
            <div key={group.label} className="mb-1">
              <div className="px-5 py-1.5">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/40">{group.label}</span>
              </div>
              {group.items.map(item => {
                const idx = counter++;
                const isSelected = idx === selectedIndex;
                return (
                  <button
                    key={item.id}
                    data-index={idx}
                    onClick={() => selectItem(item)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "w-full flex items-center gap-3 px-5 py-2.5 text-left transition-colors",
                      isSelected ? "bg-white/[0.06]" : "hover:bg-white/[0.04]"
                    )}
                  >
                    {/* ✅ Correct: HugeiconsIcon wraps the data object — never <item.icon /> */}
                    <HugeiconsIcon icon={item.icon} className="h-4 w-4 text-muted-foreground/70 flex-shrink-0" />
                    <span className="flex-1 text-[13px] text-white/90 font-medium">{item.title}</span>
                    {item.description && (
                      <span className="text-[10px] text-muted-foreground/50 mr-2">{item.description}</span>
                    )}
                    <HugeiconsIcon icon={ArrowRight} className="h-3 w-3 text-muted-foreground/30 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-2 border-t border-white/[0.06] text-[10px] text-muted-foreground/40">
          <span>↑↓ navigate · ↵ select · esc close</span>
          <span>⌘K to toggle</span>
        </div>
      </div>
    </div>
  );
}
